import React from "react";
import {
  Button,
  Alert,
  AlertIcon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from "components-lib";
import BookAppointmentModal from "views/admin/default/components/BookAppointmentModal";

// Import modular components
import PatientFormFields from "./components/forms/PatientFormFields";

// Import custom hook
import { usePatientForm } from "./components/hooks/usePatientForm";

/**
 * AddPatientForm Component - Refactored for modularity
 * 
 * This component has been modularized into:
 * - PatientFormFields: Contains all form fields and layout
 * - usePatientForm: Custom hook managing form state, validation, and API calls
 * 
 * The component maintains all original functionality while improving code organization.
 */
const AddPatientForm = ({ onPatientAdded, clinicId, doctorId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isErrorOpen, onOpen: onErrorOpen, onClose: onErrorClose } = useDisclosure();
  const { isOpen: isBookAppointmentOpen, onOpen: onBookAppointmentOpen, onClose: onBookAppointmentClose } = useDisclosure();

  // Use custom hook for form management
  const {
    newPatientData,
    errorMessage,
    fieldErrors,
    createdPatientId,
    handleInputChange,
    handleAddPatient,
    resetForm
  } = usePatientForm(clinicId, doctorId, onPatientAdded);

  // Enhanced add patient handler that manages modal flow
  const handleAddPatientWithModal = async () => {
    const success = await handleAddPatient();
    if (success) {
      onClose(); // Close the add patient modal
      onBookAppointmentOpen(); // Open the book appointment modal
    } else {
      onErrorOpen(); // Show error modal
    }
  };

  // Handle modal close with form reset
  const handleModalClose = () => {
    onClose();
    resetForm();
  };

  return (
    <>
      <Button colorScheme="teal" onClick={onOpen}>
        Add Patient
      </Button>

      {/* Add Patient Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose}>
        <ModalOverlay />
        <ModalContent maxW="600px">
          <ModalHeader>Add New Patient</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {errorMessage && (
              <Alert status="error" mb={4}>
                <AlertIcon />
                {errorMessage}
              </Alert>
            )}
            
            {/* Patient Form Fields Component */}
            <PatientFormFields
              patientData={newPatientData}
              onInputChange={handleInputChange}
              fieldErrors={fieldErrors}
            />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleAddPatientWithModal}>
              Add
            </Button>
            <Button variant="ghost" onClick={handleModalClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Error Modal */}
      <Modal isOpen={isErrorOpen} onClose={onErrorClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Error</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="error">
              <AlertIcon />
              {errorMessage}
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={onErrorClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Book Appointment Modal */}
      <BookAppointmentModal
        isOpen={isBookAppointmentOpen}
        onClose={onBookAppointmentClose}
        newAppointmentData={{
          patientId: createdPatientId || newPatientData.id,
          patientName: newPatientData.name,
          patientGender: newPatientData.gender,
          patientAge: newPatientData.age,
          phone_no: newPatientData.phoneNumber,
          appointmentDate: new Date().toISOString().split('T')[0],
        }}
      />
    </>
  );
};

export default AddPatientForm;